import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Button, Textarea } from "../ui";
import { useCoreDoc } from "../app/useCoreDoc";
import { useDocState } from "../core/useDocSelector";
import { trayAdd } from "../app/tray";
import { applyQuickAdd, quickAddPropDefs } from "../core/ops";
import { parseQuickAdd } from "../core/quickadd";
import { sharedText } from "./shared";

/**
 * Capture page (I2): `/c` for a home-screen icon, `/share` as the Android share target. One field, "Park it"
 * (to the tray) or "Add as task" (quick-add syntax). Works offline: the write goes to the local replica and
 * syncs later. No app bar, nothing else to load or look at.
 */
export function CapturePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { handle } = useCoreDoc();
  const { settled } = useDocState(handle);
  const [text, setText] = useState(() =>
    sharedText(params.get("title") ?? "", params.get("text") ?? "", params.get("url") ?? ""),
  );
  const [done, setDone] = useState<"tray" | "task" | null>(null);
  const [paused, setPaused] = useState<string | null>(null);
  // "Pause place reminders" (I9): standing in the shop and the phone keeps buzzing → one tap here
  const pausePlaces = () =>
    void fetch("/api/internal/notify/pause", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    })
      .then((r) => (r.ok ? (r.json() as Promise<{ prefs: { placePauseUntil: string | null } }>) : null))
      .then((d) => {
        const until = d?.prefs.placePauseUntil;
        if (until) setPaused(new Date(until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      });
  const source = params.has("title") || params.has("text") || params.has("url") ? "phone" : "app";
  const ready = !!handle && settled && !handle.readOnly;

  const save = (to: "tray" | "task") => {
    const v = text.trim();
    if (!v || !handle) return;
    if (to === "tray") trayAdd(handle.doc, v, source === "phone" ? "phone" : undefined);
    else {
      applyQuickAdd(
        handle.doc,
        parseQuickAdd(v, { propDefs: quickAddPropDefs(handle.doc), now: new Date() }),
      );
    }
    setText("");
    setDone(to);
  };

  return (
    <main
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col gap-3 px-5 py-8"
      data-testid="capture-page"
    >
      <h1 className="text-[20px] font-semibold">{t("capture.title")}</h1>
      <Textarea
        autoFocus
        rows={4}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setDone(null);
        }}
        placeholder={t("capture.placeholder")}
        aria-label={t("capture.title")}
        data-testid="capture-text"
      />
      <div className="flex gap-2">
        <Button disabled={!ready || !text.trim()} onClick={() => save("tray")} data-testid="capture-tray">
          {t("capture.toTray")}
        </Button>
        <Button
          variant="secondary"
          disabled={!ready || !text.trim()}
          onClick={() => save("task")}
          data-testid="capture-task"
        >
          {t("capture.toTask")}
        </Button>
      </div>
      <p role="status" className="min-h-[1.5em] text-[13px] text-ok" data-testid="capture-done">
        {done === "tray" ? t("capture.parked") : done === "task" ? t("capture.added") : ""}
      </p>
      <div className="mt-auto flex flex-col gap-2">
        <div>
          <Button size="sm" variant="ghost" onClick={pausePlaces} data-testid="capture-pause-places">
            {t("capture.pausePlaces")}
          </Button>
          {paused && (
            <span className="ml-2 text-[12.5px] text-neutral-500" data-testid="capture-paused">
              {t("capture.pausedPlaces", { until: paused })}
            </span>
          )}
        </div>
        <p className="text-[12.5px] text-neutral-500">
          {t("capture.hint")}{" "}
          <Link to="/" className="text-accent hover:underline">
            {t("capture.openApp")}
          </Link>
        </p>
      </div>
    </main>
  );
}
